import {
  LucideChevronDown,
  LucideChevronLeft,
  LucideChevronUp,
  File,
  LucideChevronRight,
} from "lucide-react";
import { NavLink, useLocation } from "react-router-dom";
import { createElement, useContext, useEffect, useMemo, useState } from "react";
import intervalLogo from "@/assets/athena-new-logo.png";
import athenaLogo from "@/assets/athena-logo-only.png";
import { SidebarMenuConfig, navConfig } from "@/layout/nav/nav-config";
import { hasCommonElement } from "@/utils/helper/hasCommonElement";
import { useCurrentUser } from "@/features/auth/useCurrentUser";
import { cn } from "@/utils/helper";
import { ThemeProviderContext } from "@/contexts/themeContext";

interface NavProps {
  className?: string;
}

export function Nav({ className }: NavProps) {
  const { collapsed, setCollapsed } = useContext(ThemeProviderContext);
  const [searchMenu, setSearchMenu] = useState<string>("");

  const additonalClasses = className ? ` ${className}` : "";
  const defaultClasses =
    "z-10 flex border-r bg-[#090B2F] dark:border-r-neutral-500 dark:bg-neutral-800 print:hidden transition-all duration-300";

  const filteredMenuConfig = useMemo(() => {
    // If searchMenu is empty, return the original navConfig
    if (!searchMenu) return navConfig;

    const filterMenu = (menu: SidebarMenuConfig[]): SidebarMenuConfig[] => {
      return (
        menu
          .map((item) => {
            // Check if the label matches the search query (case-insensitive)
            if (
              typeof item.label === "string" &&
              item.label.toLowerCase().includes(searchMenu.toLowerCase())
            ) {
              // If it matches, return the item as is (including its children)
              return item;
            }

            // If the item has children, recursively filter them
            if (item.child) {
              // Filter the children
              const filteredChild = filterMenu(item.child);
              // If any child matches, include the parent item with the filtered children
              if (filteredChild.length > 0) {
                // If the parent item itself doesn't match the search query, we can keep its original label
                return { ...item, child: filteredChild };
              }
            }

            // If neither the item nor its children match the search query, exclude it from the results
            return null;
          })
          // Filter out any null values (items that didn't match the search query)
          .filter((item): item is SidebarMenuConfig => item !== null)
      );
    };

    return filterMenu(navConfig);
  }, [searchMenu]);

  return (
    <nav
      aria-labelledby="mainnav"
      data-testid="mainnav"
      className={cn(defaultClasses, additonalClasses, {
        "w-72": !collapsed,
        "w-14": collapsed,
      })}
    >
      <div className="relative flex w-full flex-col gap-y-4">
        <header className="relative flex">
          <div
            className={cn("relative flex flex-col gap-y-2", {
              "px-5": !collapsed,
              "px-2 py-5": collapsed,
            })}
          >
            <img
              src={intervalLogo}
              className={cn({
                hidden: collapsed,
              })}
              alt="Interval: Integrated & Validated"
            />
            <img
              src={athenaLogo}
              className={cn(
                {
                  hidden: !collapsed,
                },
                "w-full",
              )}
              alt="Athena Logo"
            />

            <div className={cn("w-full", { hidden: collapsed })}>
              <input
                type="text"
                placeholder="Search..."
                onChange={(e) => setSearchMenu(e.target.value)}
                className="h-8 w-full rounded bg-[#515378] p-2 text-white"
              />
            </div>

            <h2 id="mainnav" className="sr-only">
              Main Navigation
            </h2>
          </div>
        </header>
        <NavList
          list={filteredMenuConfig}
          className="h-full overflow-y-auto scrollbar scrollbar-thumb-axiata-grey scrollbar-thumb-rounded-full scrollbar-w-1.5"
        />
      </div>

      <div className="absolute -right-3.5 top-8 z-20 aspect-square overflow-hidden rounded-full bg-purple-500 shadow">
        <button
          onClick={() => setCollapsed((prev) => !prev)}
          className="aspect-square overflow-hidden p-1 font-bold text-white active:outline-none"
        >
          {collapsed ? (
            <LucideChevronRight size={20} />
          ) : (
            <LucideChevronLeft size={20} />
          )}
        </button>
      </div>
    </nav>
  );
}

interface NavListProps {
  list: SidebarMenuConfig[];
  hasParent?: boolean;
  className?: string;
}

function NavList({ list, hasParent = false, className = "" }: NavListProps) {
  return (
    <ul className={cn("flex flex-col", className)}>
      {list.map((item, index) =>
        item.child ? (
          <NavItemParent key={item.href + index} item={item} />
        ) : (
          <NavItem key={item.href + index} item={item} isHome={!hasParent} />
        ),
      )}
    </ul>
  );
}

interface NavItemProps {
  item: SidebarMenuConfig;
  isHome?: boolean;
}

function NavItem({ item, isHome }: NavItemProps) {
  const { collapsed } = useContext(ThemeProviderContext);
  const { roles } = useCurrentUser();
  const [isActivePage, setIsActivePage] = useState<boolean>(false);
  const label =
    typeof item.label === "function" ? item.label(roles) : item.label;

  const location = useLocation();

  useEffect(() => {
    if (item.href !== "/") {
      const isActivePage = window.location.pathname.includes(item.href);
      setIsActivePage(isActivePage);
    }
  }, [location, item]);

  if (
    !roles ||
    !item.allowedRoles ||
    !hasCommonElement(item.allowedRoles, roles)
  ) {
    return null;
  }

  return (
    <li className="rounded-sm">
      <NavLink
        to={item.href}
        className={cn(
          `nav-item ${
            isHome ? "px-2 text-base" : "px-4 text-sm"
          } items-center gap-x-2 rounded-sm border-b border-white border-opacity-10 py-2 text-white hover:bg-[#D9D9D9] hover:bg-opacity-35 dark:text-neutral-300 ${
            isActivePage && "border-0 bg-[#D9D9D9] bg-opacity-35 text-white"
          }`,
          {
            "justify-center": collapsed,
            "justify-between": !collapsed,
            "pr-2": !collapsed,
          },
        )}
        end
      >
        <div className="flex gap-x-2">
          {
            <div className="nav-icon">
              {item.icon ?? createElement(File, { width: 20, height: 20 })}
            </div>
          }

          <div className={cn("nav-label", { hidden: collapsed })}>{label}</div>
        </div>
      </NavLink>
    </li>
  );
}

interface NavItemParentProps {
  item: SidebarMenuConfig;
}

function NavItemParent({ item }: NavItemParentProps) {
  const [showChildren, setShowChildren] = useState(true);
  const { collapsed } = useContext(ThemeProviderContext);
  const { roles } = useCurrentUser();
  const label =
    typeof item.label === "function" ? item.label(roles) : item.label;

  // Helper function to check if any child has access
  const hasAccessibleChild = (children: SidebarMenuConfig[]): boolean => {
    return children.some((child) => {
      // If child has nested children, check recursively
      if (child.child) {
        return hasAccessibleChild(child.child);
      }

      // Check if current child is accessible
      return (
        roles &&
        child.allowedRoles &&
        hasCommonElement(child.allowedRoles, roles)
      );
    });
  };

  if (
    !roles ||
    !item.child ||
    (item.child && !hasAccessibleChild(item.child))
  ) {
    return null;
  }

  function handleShowChildren() {
    setShowChildren(!showChildren);
  }

  return (
    <li className="flex flex-col gap-y-2">
      <button
        className={cn(
          `nav-item items-center gap-x-2 border-b border-white border-opacity-20 py-2 text-white dark:text-neutral-300`,
          {
            "justify-center": collapsed,
            "gap-x-0": collapsed,
            "justify-between": !collapsed,
            "pr-2": !collapsed,
          },
        )}
        onClick={handleShowChildren}
      >
        <div className={cn("flex gap-x-2")}>
          <div className="nav-icon">{item.icon}</div>
          <div className={cn("nav-label flex gap-x-2", { hidden: collapsed })}>
            {label}
          </div>
        </div>
        <div className={cn("nav-arrow")}>
          {showChildren ? <LucideChevronUp /> : <LucideChevronDown />}
        </div>
      </button>
      {showChildren && (
        <NavList
          list={item.child!}
          hasParent={true}
          className={cn({ "pl-2": !collapsed })}
        />
      )}
    </li>
  );
}
